import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { ReceiveEvidenceRequest } from '../../../types/custody-actions'
import { usePerformCustodyAction } from '../../../hooks/use-custody-actions'
import { UnlinkedEvidenceDialog } from './UnlinkedEvidenceDialog'
import {
  type ReceiveFormState,
  type Updater,
  ReceiveSelectorSection,
  ReceiveDateTimeSection,
  ReceiveNotesSection,
} from './ReceiveEvidenceForm.parts'

interface ReceiveEvidenceFormProps {
  caseId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface PartyFieldsProps {
  prefix: string
  nameValue: string
  roleValue: string
  orgValue: string
  onNameChange: (val: string) => void
  onRoleChange: (val: string) => void
  onOrgChange: (val: string) => void
}

function PartyFields({
  prefix,
  nameValue,
  roleValue,
  orgValue,
  onNameChange,
  onRoleChange,
  onOrgChange,
}: PartyFieldsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <fieldset className="space-y-2 rounded-lg border border-[var(--border)] p-3">
      <legend className="px-1 text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
        {prefix}
      </legend>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label htmlFor={`${prefix}-name`} className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.name')}
          </label>
          <input
            id={`${prefix}-name`}
            type="text"
            value={nameValue}
            onChange={(ev) => {
              onNameChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-role`} className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.role')}
          </label>
          <input
            id={`${prefix}-role`}
            type="text"
            value={roleValue}
            onChange={(ev) => {
              onRoleChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-org`} className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.organisation')}
          </label>
          <input
            id={`${prefix}-org`}
            type="text"
            value={orgValue}
            onChange={(ev) => {
              onOrgChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
    </fieldset>
  )
}

const DATE_SLICE_END = 10
const TIME_SLICE_END = 5

function buildInitialState(): ReceiveFormState {
  const now = new Date()
  return {
    selectedIds: [],
    completionType: 'complete',
    actionDate: now.toISOString().slice(0, DATE_SLICE_END),
    actionTime: now.toTimeString().slice(0, TIME_SLICE_END),
    fromName: '',
    fromRole: '',
    fromOrg: '',
    toName: '',
    toRole: '',
    toOrg: '',
    method: '',
    conditionNotes: '',
    notes: '',
  }
}

function buildRequest(state: ReceiveFormState): ReceiveEvidenceRequest {
  const req: ReceiveEvidenceRequest = {
    evidenceNumberIds: state.selectedIds,
    completionType: state.completionType,
    actionDate: state.actionDate,
    actionTime: state.actionTime,
    fromName: state.fromName,
    toName: state.toName,
  }
  if (state.fromRole.length > 0) req.fromRole = state.fromRole
  if (state.fromOrg.length > 0) req.fromOrganisation = state.fromOrg
  if (state.toRole.length > 0) req.toRole = state.toRole
  if (state.toOrg.length > 0) req.toOrganisation = state.toOrg
  if (state.method.length > 0) req.method = state.method
  if (state.conditionNotes.length > 0) req.conditionNotes = state.conditionNotes
  if (state.notes.length > 0) req.notes = state.notes
  return req
}

interface ReceiveFormFieldsProps {
  caseId: string
  state: ReceiveFormState
  update: Updater
  error: string | null
  linkageResolved: boolean
}

function ReceiveFormFields({
  caseId,
  state,
  update,
  error,
  linkageResolved,
}: ReceiveFormFieldsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="space-y-4 overflow-y-auto p-6">
      <ReceiveSelectorSection caseId={caseId} state={state} update={update} linkageResolved={linkageResolved} />
      <ReceiveDateTimeSection state={state} update={update} />
      <PartyFields
        prefix={t('custodyActions.from')}
        nameValue={state.fromName}
        roleValue={state.fromRole}
        orgValue={state.fromOrg}
        onNameChange={(val) => { update('fromName', val) }}
        onRoleChange={(val) => { update('fromRole', val) }}
        onOrgChange={(val) => { update('fromOrg', val) }}
      />
      <PartyFields
        prefix={t('custodyActions.to')}
        nameValue={state.toName}
        roleValue={state.toRole}
        orgValue={state.toOrg}
        onNameChange={(val) => { update('toName', val) }}
        onRoleChange={(val) => { update('toRole', val) }}
        onOrgChange={(val) => { update('toOrg', val) }}
      />
      <ReceiveNotesSection state={state} update={update} error={error} />
    </div>
  )
}

interface ReceiveFormCallbacks {
  onClose: () => void
  onSuccess: () => void
}

interface ReceiveFormLogic {
  state: ReceiveFormState
  update: Updater
  linkageResolved: boolean
  hook: ReturnType<typeof usePerformCustodyAction>
  handleGenerateCr: (notes: string) => Promise<void>
  handleSubmit: () => Promise<void>
  handleClose: () => void
  showDialog: boolean
  submitDisabled: boolean
}

function useReceiveFormLogic(caseId: string, callbacks: ReceiveFormCallbacks): ReceiveFormLogic {
  const [state, setState] = useState<ReceiveFormState>(buildInitialState)
  const [linkageResolved, setLinkageResolved] = useState(false)
  const hook = usePerformCustodyAction()

  const update = useCallback(
    <K extends keyof ReceiveFormState>(key: K, value: ReceiveFormState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  // Destructure stable useCallback handles from the hook so the effect below
  // does not depend on the hook object itself. `usePerformCustodyAction()`
  // returns a fresh object literal on every render — including `hook` in the
  // dep array would re-fire the effect on every state transition inside the
  // hook (including the `setChecking(true|false)` flips it triggers itself),
  // creating an infinite linkage-check loop that keeps `checking` effectively
  // true forever and disables the submit button. `checkLinkage` and
  // `clearLinkageResult` are wrapped in `useCallback([])` so they are stable.
  const { checkLinkage, clearLinkageResult } = hook

  useEffect(() => {
    if (state.selectedIds.length === 0) {
      clearLinkageResult()
      setLinkageResolved(false)
      return
    }
    void checkLinkage(caseId, state.selectedIds)
  }, [state.selectedIds, caseId, checkLinkage, clearLinkageResult])

  const handleGenerateCr = useCallback(
    async (notes: string) => {
      const ids = hook.linkageResult?.unlinkedItems.map((i) => i.evidenceNumberId) ?? []
      const result = await hook.createExternalCr(caseId, { evidenceNumberIds: ids, notes })
      if (result !== null) {
        await hook.checkLinkage(caseId, state.selectedIds)
        setLinkageResolved(true)
      }
    },
    [caseId, hook, state.selectedIds]
  )

  const handleSubmit = useCallback(async () => {
    const result = await hook.performReceive(caseId, buildRequest(state))
    if (result !== null) callbacks.onSuccess()
  }, [state, caseId, hook, callbacks])

  const handleClose = useCallback(() => {
    hook.clearError()
    hook.clearLinkageResult()
    setLinkageResolved(false)
    callbacks.onClose()
  }, [hook, callbacks])

  const showDialog = hook.linkageResult !== null && !hook.linkageResult.allLinked
  const submitDisabled =
    hook.performing || hook.checking || showDialog || state.selectedIds.length === 0

  return {
    state,
    update,
    linkageResolved,
    hook,
    handleGenerateCr,
    handleSubmit,
    handleClose,
    showDialog,
    submitDisabled,
  }
}

interface ReceiveFormFooterProps {
  submitDisabled: boolean
  onClose: () => void
  onSubmit: () => void
}

function ReceiveFormFooter({
  submitDisabled,
  onClose,
  onSubmit,
}: ReceiveFormFooterProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  return (
    <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded border border-[var(--border)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
      >
        {t('custodyActions.cancel')}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitDisabled}
        className="rounded bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-white uppercase hover:opacity-90 disabled:opacity-50"
      >
        {t('custodyActions.submit')}
      </button>
    </div>
  )
}

export function ReceiveEvidenceForm({
  caseId,
  open,
  onClose,
  onSuccess,
}: ReceiveEvidenceFormProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const {
    state,
    update,
    linkageResolved,
    hook,
    handleGenerateCr,
    handleSubmit,
    handleClose,
    showDialog,
    submitDisabled,
  } = useReceiveFormLogic(caseId, { onClose, onSuccess })

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
            {t('custodyActions.receive')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {hook.checking && (
          <div
            role="status"
            aria-busy="true"
            className="border-b border-[var(--border)] px-6 py-2 text-xs text-[var(--text-muted)]"
          >
            {t('custodyActions.linkageCheck.checkingLinkage')}
          </div>
        )}
        <ReceiveFormFields
          caseId={caseId}
          state={state}
          update={update}
          error={hook.error}
          linkageResolved={linkageResolved}
        />
        <ReceiveFormFooter
          submitDisabled={submitDisabled}
          onClose={handleClose}
          onSubmit={() => { void handleSubmit() }}
        />
      </div>
      {showDialog && hook.linkageResult && (
        <UnlinkedEvidenceDialog
          open={showDialog}
          unlinkedItems={hook.linkageResult.unlinkedItems}
          onCancel={handleClose}
          onGenerateCr={(notes) => { void handleGenerateCr(notes) }}
          generating={hook.creatingCr}
        />
      )}
    </div>
  )
}
